import React, { useEffect, useState } from "react";
import BaseCard from "../../../components/ui/BaseCard";
import { TextField, InputAdornment } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchUserSkills } from "../../../services/api/user";
import { useParams } from "react-router-dom";
import BaseTooltip from "../../../components/ui/BaseTooltip";
import { ModuleType, UserType } from "../../../utils/enum";
import useAuth from "../../../hooks/useAuth";
import AddSkillsModal from "../../CompleteYourProfile/components/AddSkillsModal";
import { verifyOrUnverifyEngineerSkill } from "../../../services/api/admin";
import { useDispatch } from "react-redux";
import { showToast } from "../../../store/toast/toastSlice";

const SkillSet = ({ userId }) => {
  const dispatch = useDispatch();
  const {
    isPending: isUserSkillsLoading,
    error: isUserSkillsError,
    refetch: refetchUserSkills,
    data: userSkillsData,
  } = useQuery({
    queryKey: ["userSkillsData"],
    queryFn: () => fetchUserSkills(userId),
    retry: 2,
    enabled: !!userId,
  });

  const {
    isPending: isVerifyOrUnverifyEngineerSkillMutationApiLoading,
    error: isVerifyOrUnverifyEngineerSkillMutationApiError,
    mutateAsync: verifyOrUnverifyEngineerSkillMutation,
  } = useMutation({
    mutationFn: verifyOrUnverifyEngineerSkill,
    enabled: false,
  });

  const { authRole, getPermissionForModule } = useAuth();
  const { hasWriteAccess: hasEngineerSkillsSetWriteAccess } = getPermissionForModule(
    ModuleType.ENGINEER_SKILLS_SET,
  );
  const [expanded, setExpanded] = useState(false);
  const [isAddSkillsModalOpen, setIsAddSkillsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const MAX_ITEMS = 8;

  const filteredSkills = userSkillsData?.data?.filter((skill) =>
    skill?.subCategory?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleVerifyOrUnverifySkill = async (payload) => {
    try {
      await verifyOrUnverifyEngineerSkillMutation(payload);
      refetchUserSkills();
    } catch (error) {
      dispatch(
        showToast({
          message: error.response.data.message,
          type: "error",
        }),
      );
    }
  };

  return (
    <BaseCard className="py-2 px-8 sm:px-10 rounded-lg" elevation={1}>
      <div className="bg-light -mx-4 px-4 py-2 mt-2 mb-4 rounded-md flex justify-between items-center">
        <h6 className="text-dark font-normal">Skill Set</h6>
        {(authRole === UserType.ADMIN || hasEngineerSkillsSetWriteAccess) && (
          <i
            className="fa-light fa-pencil text-gray-900 cursor-pointer"
            onClick={() => setIsAddSkillsModalOpen(true)}
          ></i>
        )}
      </div>

      {/* Search Input */}
      <TextField
        placeholder="Search"
        className="bg-light rounded-md py-1.5 md:w-64 "
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <i className="fa-solid fa-search text-[#B0B0B0]"></i>
            </InputAdornment>
          ),
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            "& fieldset": { borderColor: "transparent" },
            "&:hover fieldset": { borderColor: "transparent" },
            "&.Mui-focused fieldset": { borderColor: "transparent" },
          },
        }}
      />

      <div className={`flex flex-wrap mt-5 overflow-hidden ${expanded ? "h-auto" : ""}`}>
        {(expanded ? filteredSkills : filteredSkills?.slice(0, MAX_ITEMS))?.map((skill, index) => (
          <p
            key={index}
            className="bg-light py-1 px-4 rounded-md mr-4 mb-2 cursor-default whitespace-nowrap flex items-center"
          >
            {skill?.subCategory}
            {(UserType.ADMIN === authRole || UserType.CLIENT === authRole) && (
              <BaseTooltip
                content={
                  <p className="text-white">{skill?.isVerified ? "Verified" : "Not Verified"}</p>
                }
              >
                {!isVerifyOrUnverifyEngineerSkillMutationApiLoading ? (
                  skill?.isVerified ? (
                    <i
                      className="fa-solid text-primary fa-circle-check cursor-pointer ml-2"
                      tabIndex={0}
                      role="button"
                      onClick={() => {
                        if (authRole === UserType.ADMIN || hasEngineerSkillsSetWriteAccess) {
                          handleVerifyOrUnverifySkill({
                            userSkillId: skill.id,
                            isVerified: !skill?.isVerified,
                          });
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          if (authRole === UserType.ADMIN || hasEngineerSkillsSetWriteAccess) {
                            handleVerifyOrUnverifySkill({
                              userSkillId: skill.id,
                              isVerified: !skill?.isVerified,
                            });
                          }
                        }
                      }}
                    ></i>
                  ) : (
                    <i
                      className="fa-light fa-circle cursor-pointer ml-2"
                      tabIndex={0}
                      role="button"
                      onClick={() => {
                        if (authRole === UserType.ADMIN || hasEngineerSkillsSetWriteAccess) {
                          handleVerifyOrUnverifySkill({
                            userSkillId: skill.id,
                            isVerified: !skill?.isVerified,
                          });
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          if (authRole === UserType.ADMIN || hasEngineerSkillsSetWriteAccess) {
                            handleVerifyOrUnverifySkill({
                              userSkillId: skill.id,
                              isVerified: !skill?.isVerified,
                            });
                          }
                        }
                      }}
                    ></i>
                  )
                ) : (
                  <p>Loading...</p>
                )}
              </BaseTooltip>
            )}
          </p>
        ))}
      </div>

      {/* View More / View Less Button */}
      {filteredSkills?.length > MAX_ITEMS && (
        <p className="text-primary cursor-pointer mt-2" onClick={() => setExpanded(!expanded)}>
          {expanded ? "View Less" : "View More"}
        </p>
      )}

      <AddSkillsModal
        close={setIsAddSkillsModalOpen}
        open={isAddSkillsModalOpen}
        refetchUserSkills={refetchUserSkills}
        skills={userSkillsData?.data}
        userId={userId}
      />
    </BaseCard>
  );
};

export default SkillSet;
